"use client";

import Image from "next/image";
import { ReactNode, useEffect, useState } from "react";

interface AuthorData {
  id: string;
  userId: string;
  avatar: string | null;
  firstName: string;
  lastName: string;
  fullName: string;
  designation: string;
  officeEmail: string;
  description: string;
  userEmail: string;
  userName: string;
}

interface AboutAuthorProps {
  authorName: string;
  formattedDate?: string;
  footerActions?: ReactNode;
}

const AboutAuthor: React.FC<AboutAuthorProps> = ({
  authorName,
  formattedDate,
  footerActions
}) => {
  const [author, setAuthor] = useState<AuthorData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAuthor = async () => {
      if (!authorName.trim()) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const encoded = encodeURIComponent(authorName);
        const res = await fetch(`/api/authors/${encoded}`);

        if (!res.ok) {
          if (res.status === 404) return setAuthor(null);
          throw new Error("Failed to fetch author details");
        }

        const data = await res.json();
        if (data.success && data.author) {
          setAuthor(data.author);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Something went wrong");
        setAuthor(null);
      } finally {
        setLoading(false);
      }
    };

    fetchAuthor();
  }, [authorName]);

  // Always render the section, even if author details aren't found
  // This ensures footer information is always displayed
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 mt-8 overflow-hidden">
      {/* Author Section */}
      {!loading && !error && author && (
        <div className="p-6 md:p-8 border-b border-gray-100">
          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <span className="w-1 h-6 bg-[#E0301E] rounded-full"></span>
            About the Author
          </h3>

          <div className="flex flex-col md:flex-row gap-6">
            {/* Avatar */}
            <div className="flex-shrink-0">
              <div className="relative w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden shadow-lg bg-gradient-to-br from-blue-100 to-blue-300 border-4 border-white ring-2 ring-gray-100">
                {author.avatar ? (
                  <Image
                    src={author.avatar}
                    alt={author.fullName}
                    fill
                    className="object-cover"
                    onError={(e) => {
                      const target = e.currentTarget as HTMLImageElement;
                      target.style.display = 'none';
                      const container = target.parentElement;
                      if (container) {
                        const fallback = container.querySelector('.author-fallback');
                        if (fallback) {
                          (fallback as HTMLElement).style.display = 'flex';
                        }
                      }
                    }}
                  />
                ) : null}
                <div
                  className="author-fallback w-full h-full items-center justify-center text-2xl md:text-3xl font-bold text-blue-600"
                  style={{ display: author.avatar ? 'none' : 'flex' }}
                >
                  {author.firstName?.charAt(0)?.toUpperCase() || authorName.charAt(0).toUpperCase()}
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">


              {author.description && author.description.trim() ? (
                <p className="text-gray-700 text-sm md:text-base leading-snug ">
                  {author.description}
                </p>
              ) : (
                <p className="text-gray-500 italic text-sm md:text-base">
                  No description available for this author.
                </p>
              )}

              <div className="mt-3">
                <h4 className="text-lg  font-medium text-blue-500 inline-block">
                  {author.fullName || authorName}
                </h4>
                {author.designation && (
                  <p className="text-[#5060A1] text-sm md:text-base font-light inline-block ml-2">
                    -{author.designation}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer Section */}
      <div className="p-6  bg-gray-50">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          {/* Publication Info */}
          <div className="flex-1">
            {formattedDate && (
              <p className="text-sm text-gray-600 font-medium">
                Published on <span className="font-semibold text-gray-900">{formattedDate}</span>
                {authorName && (
                  <> by <span className="font-semibold text-gray-900">{authorName}</span></>
                )}
              </p>
            )}
            {!formattedDate && authorName && (
              <p className="text-sm text-gray-600 font-medium">
                Written by <span className="font-semibold text-gray-900">{authorName}</span>
              </p>
            )}
          </div>

          {/* Action Buttons */}
          {footerActions && (
            <div className="flex flex-col sm:flex-row gap-3">
              {footerActions}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AboutAuthor;
